/**
 * DTO completo para guardar/cargar protocolos y órdenes de trabajo
 * Coincide exactamente con el DTO del backend - NUEVA ESTRUCTURA
 */
export interface ProtocolOrderDto {
    id: string | null;
    clientId: number | null;
    vehicleId: number | null;
    employeeId: number | null;
    agendaId: number | null;
    externalId: string | null;
    protocolOrderStructureDto: ProtocolOrderStructureDto;
    workOrderStructureDto: WorkOrderStructureDto;
    exitProtocolOrderStructureDto?: ProtocolOrderStructureDto; // Opcional para protocolos de salida
}

export interface ProtocolOrderStructureDto {
    protocolOrderStatus: number;
    protocolType: number;
    generalProgress: number;
    startDate: string | null;
    lastModification: string | null;
    endDate: string | null;
    documentUrls?: string[];

    clientType: number | null;
    clientDocumentType: number;
    clientIdDocument: string;
    clientFullName?: string;
    clientCity: string;
    clientAddress: string;
    clientPhone: string;
    clientEmail: string;
    gender: string | null;
    birthday: string | null;

    vehicleLicensePlate: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleColor: string;
    vehicleVin: string;
    vehicleKilometers: number | null;
    vehicleFuelLevel?: number;
    vehicleDepositLevel?: number | null;
    vehicleType: number;
    vehicleRegistrationDate: string | null;
    vehicleFuelType: number;
    vehicleAssuranceActive: boolean;
    manufacturingDate: string | null;

    authorizedPersonName: string | null;
    authorizedPersonIdDocument: string | null;
    authorizedPersonPhone: string | null;

    vehicleTechnicalSheetPicture: string | null;
    vehicleRegistrationDocumentPicture: string | null;
    vehicleInsurancePicture: string | null;
    vehicleITVPicture: string | null;

    clientResponsabilityIntervention: boolean;
    assuranceResponsabilityIntervention: boolean;
    assuranceName: string;
    claimNumber: string;
    assignedExpert: string;
    insuranceCoverage: number;

    onlinePayment: boolean;
    requiresPayment: boolean | null;
    estimatedPrice: number | null;
    paymentType: number | null;

    depositAmount?: number | null;
    isDepositPaid?: boolean | null;

    vehicleReceptionDate: string | null;
    vehicleEstimatedDeliveryDate: string;

    requestedService: number | null;
    additionalServices: unknown[];
    serviceName: string;
    serviceTypeId?: number | null;
    observations: string;

    frontVehiclePicture: string | null;
    leftFrontSideVehiclePicture: string | null;
    leftRearSideVehiclePicture: string | null;
    rightFrontSideVehiclePicture: string | null;
    rightRearSideVehiclePicture: string | null;
    trunkVehiclePicture: string | null;
    frontWindShieldVehiclePicture: string | null;
    roofVehiclePicture: string | null;
    frontVehicleHasDamage: boolean | null;
    leftFrontSideVehicleHasDamage: boolean | null;
    leftRearSideVehicleHasDamage: boolean | null;
    rightFrontSideVehicleHasDamage: boolean | null;
    rightRearSideVehicleHasDamage: boolean | null;
    trunkVehicleHasDamage: boolean | null;
    frontWindShieldVehicleHasDamage: boolean | null;
    roofVehicleHasDamage: boolean | null;
    extraVehiclePictures: string[];
    extraVehiclePicturesDescriptions: string[];

    // Inspección visual
    visualInspectionObservations: string | null;

    // Campos específicos de depósito (protocolo de custodia) - OPCIONALES
    depositType?: string | null; // 'repair-without-budget', 'request-budget', 'repair-with-approved-budget'

    /** @deprecated Redundant with depositType="repair-without-budget". Will be removed in future version. */
    waivePriorEstimate?: boolean;

    roadTestAuthorization?: boolean | null;
    keepReplacedParts1?: boolean | null; // Campo unificado: "¿Desea conservar las piezas sustituidas?"
    requestEstimateBeforeRepair?: boolean | null;
    approvedEstimateNumber?: string | null;

    /** @deprecated Redundant with depositType="repair-with-approved-budget". Will be removed in future version. */
    repairAuthorizationWithEstimate?: boolean;

    // Autorizaciones adicionales sobre piezas
    authorizationRebuiltParts?: boolean | null; // Autorizar piezas reconstruidas
    authorizationNonOriginalParts?: boolean | null; // Autorizar piezas no originales

    /** @deprecated Use keepReplacedParts1 instead. Kept for backward compatibility with legacy data. */
    waiveKeepReplacedParts?: boolean;

    /** @deprecated Use keepReplacedParts1 instead. Kept for backward compatibility with legacy data. */
    keepReplacedParts2?: boolean;

    // Firmas
    clientSignaturePicture: string | null;
    centerSignaturePicture: string | null;

    // Opciones de finalización
    sendDocumentToClient: boolean | null;
    printSummaryWithNoImages: boolean | null;
}

/**
 * Estructura específica para órdenes de trabajo (cambio de neumáticos)
 */
export interface WorkOrderStructureDto {
    // Campos de control del protocolo (movidos desde nivel superior)
    protocolOrderStatus: number;
    protocolType: number;
    generalProgress: number;
    startDate: string;
    lastModification: string;
    endDate: string;
    documentUrls?: string[];

    // Checklist de procedimientos de cambio de neumáticos
    handbrakeApplied: boolean;
    vehicleOnFlatSurface: boolean;
    wheelChocksPlaced: boolean;
    liftingMethod: boolean;
    hubcapRemoved: boolean;
    boltsRemoved: boolean;
    wheelRemoved: boolean;
    tireDeflated: boolean;
    tireDismounted: boolean;
    oldValveRemoved: boolean;
    rimCleaned: boolean;
    newValveInstalled: boolean;
    tireDimensionsChecked: boolean;
    tireMounted: boolean;
    rotationDirection: boolean;
    tireInflated: boolean;
    wheelBalanced: boolean;
    wheelMounted: boolean;
    rimBolted: boolean;
    finalPressureChecked: boolean;
    vehicleOnAlignmentRack: boolean;
    alignmentParametersAdjusted: boolean;
    alignmentReport: boolean;
    alignmentReportPhotoAttached: boolean;
    tireRotationDone: boolean;

    // Fotos específicas del cambio de neumáticos
    finalPressureCheckedPicture: string | null;
    installedTirePhotoPicture: string | null;
    checklistCompletedPicture: string | null;
    additionalPictures: string[] | null;
}

/**
 * Enums para los tipos de protocolo
 */
export enum ProtocolType {
    Custody = 0,        // 0 para custodia
    Reception = 1,      // 1 para recepción  
    TyreChange = 2,     // 2 para cambio de neumáticos
    Exit = 3,           // 3 para salida
    Checklist = 4       // 4 para checklist de inspección
}

/**
 * Enums para el estado del protocolo
 */
export enum ProtocolOrderStatus {
    Pending = 0,        // 0 para pending (no empezado) - NO se usa en la aplicación
    InProgress = 1,     // 1 para en progreso
    Completed = 2       // 2 para finalizado
}

/**
 * Enums para tipos de documento del cliente
 */
export enum ClientDocumentType {
    DNI = 0,
    NIE = 1,
    Passport = 2,
    RUT = 3,
    CIF = 4,
    Other = 5,
    TWID = 13,
    TWARC = 14,
    /** Alinhado com `DocumentType.PT_CARTAO` (backend) */
    PT_CARTAO = 15
}

/**
 * Enums para tipos de combustible
 */
export enum FuelType {
    Gasoline = 0,
    Diesel = 1,
    Electric = 2,
    Hybrid = 3,
    Gas = 4,
    Other = 5
}

/**
 * Enums para tipos de vehículo
 */
export enum VehicleType {
    Car = 0,
    Motorcycle = 1,
    Truck = 2,
    Van = 3,
    Bus = 4,
    Other = 5
}

/**
 * Enums para tipos de pago
 */
export enum PaymentType {
    Free = 0,
    Advance = 1,
    After = 2,
    Insurance = 3,
    PartialAdvance = 4
}

/**
 * Enums para cobertura de seguro
 */
export enum InsuranceCoverage {
    None = 0,
    Partial = 1,
    Full = 2
}

/**
 * Mapeo de ProtocolType del frontend a query param del backend
 * Backend acepta: entry | work | exit | checklist
 */
export const PROTOCOL_TYPE_TO_QUERY_PARAM: Record<ProtocolType, string | null> = {
    [ProtocolType.Custody]: 'entry',        // Custodia usa el mismo pipeline de entrada en backend
    [ProtocolType.Reception]: 'entry',      // Protocolo de entrada
    [ProtocolType.TyreChange]: 'work',      // Protocolo de trabajo
    [ProtocolType.Exit]: 'exit',            // Protocolo de salida
    [ProtocolType.Checklist]: 'checklist'   // Checklist de inspección
} as const;

/**
 * Obtiene el query param correspondiente a un ProtocolType
 * @param type - Tipo de protocolo del frontend
 * @returns string del query param o null si no tiene mapeo
 */
export function getProtocolTypeQueryParam(type: ProtocolType): string | null {
    return PROTOCOL_TYPE_TO_QUERY_PARAM[type];
}
